// src/pages/PaymentSelection.js
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import { UserContext } from '../context/UserContext';
import { CartContext } from '../context/CartContext';
import { NotificationContext } from '../context/NotificationContext';
import { apiFetch } from '../utils/apiClient';
import { BASE_URL } from '../utils/config';
import { formatCurrency } from '../utils/priceCalculator';
import { handleAbandonedOrderCheck } from '../utils/orderUtils';
import './PaymentSelection.css';

const PAYMENT_IN_PROGRESS_FLAG = 'paymentInProgress';

const PaymentSelection = () => {
  const { t } = useTranslation();
  const lang = useLanguage();
  const [orderId, setOrderId] = useState(null);
  const [order, setOrder] = useState(null);
  const [loadingProvider, setLoadingProvider] = useState(null); // null, 'rivhit', or 'paypal'
  const [error, setError] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { user, updateUser, refreshUser } = useContext(UserContext);
  const { fetchCart } = useContext(CartContext);
  const { addNotification } = useContext(NotificationContext);

  // This ref will persist across the StrictMode unmount/remount cycle.
  const unmountRef = useRef(false);
  const orderIdRef = useRef(null); // Ref to hold the orderId for the cleanup function.

  // Create refs to hold the latest context functions.
  // This prevents the main effect from re-running when the user object changes (e.g., token refresh).
  const userRef = useRef(user);
  const updateUserRef = useRef(updateUser);
  const refreshUserRef = useRef(refreshUser);
  const fetchCartRef = useRef(fetchCart);
  const addNotificationRef = useRef(addNotification);
  const tRef = useRef(t);
  useEffect(() => {
    userRef.current = user;
    updateUserRef.current = updateUser;
    refreshUserRef.current = refreshUser;
    fetchCartRef.current = fetchCart;
    addNotificationRef.current = addNotification;
    tRef.current = t;
  });

  // Effect to initialize component state from URL and handle page load logic.
  useEffect(() => {
    // On mount, clear any stale flags from previous attempts.
    // This also handles the case where a user cancels on the payment provider's site and returns here.
    sessionStorage.removeItem(PAYMENT_IN_PROGRESS_FLAG);

    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get('orderId');
    if (id) {
      setOrderId(id);
      orderIdRef.current = id; // Store the ID in a ref.
    } else {
      setError(t('paymentSelection.noOrderIdError'));
      addNotification(t('paymentSelection.noOrderIdNotification'), 'error');
    }
  }, [location, addNotification, t]);

  // Effect to fetch order details once we have an orderId
  useEffect(() => {
    if (!orderId || !user.token) return;

    const fetchOrderDetails = async () => {
      try {
        const response = await apiFetch(`${BASE_URL}/api/orders/${orderId}/status`, {}, user.token);
        if (!response.ok) {
          throw new Error(t('paymentSelection.fetchOrderFailed'));
        }
        const data = await response.json();
        setOrder(data.order); // The API returns { order, user }
      } catch (err) {
        setError(err.message);
        addNotification(err.message, 'error');
      }
    };

    fetchOrderDetails();
  }, [orderId, user.token, addNotification, t]);

  // Effect to handle abandoning the order on unmount.
  // It has an empty dependency array so it only runs its cleanup function once when the component truly unmounts.
  useEffect(() => {
    console.log('[PaymentSelection] Mount effect is running.');
    unmountRef.current = false; // On mount or remount, flag that we are mounted.

    return () => {
      console.log('[PaymentSelection] Cleanup function is starting.');
      unmountRef.current = true; // On unmount, flag that we are unmounting.

      // By using a microtask, we delay the check. If a remount happens,
      // the effect above will set unmountRef.current back to false before this check occurs.
      // If it's a real unmount, the flag will remain true.
      queueMicrotask(async () => {
        console.log(`[PaymentSelection] Microtask running. Is component unmounted? ${unmountRef.current}`);
        if (unmountRef.current) {
          // Check if a payment process has been initiated. If so, do not abandon the order.
          if (sessionStorage.getItem(PAYMENT_IN_PROGRESS_FLAG)) {
            console.log('[PaymentSelection] Microtask: Payment in progress, not abandoning order.');
            return;
          }

          // --- NEW LOGIC: Use the reusable abandoned order check ---
          const context = {
            user: userRef.current,
            updateUser: updateUserRef.current,
            refreshUser: refreshUserRef.current,
            fetchCart: fetchCartRef.current,
            addNotification: addNotificationRef.current,
            t: tRef.current,
          };
          console.log('[PaymentSelection] Microtask: Navigating away. Calling handleAbandonedOrderCheck.');
          handleAbandonedOrderCheck(context);

          // --- OLD LOGIC (Commented out as requested) ---
          /*
          const currentOrderId = orderIdRef.current;

          if (currentOrderId) {
            try {
              console.log(`[PaymentSelection] Microtask: Navigating away. Attempting to abandon order ${currentOrderId}.`);
              const token = userRef.current.token;
              const response = await apiFetch(`${BASE_URL}/api/orders/${currentOrderId}/abandon`, { method: 'DELETE' }, token);
              const result = await response.json();
              if (response.ok && result.user) {
                updateUserRef.current(result.user);
                console.log(`[PaymentSelection] Microtask: Order abandoned successfully. User context updated.`);
              }
            } catch (err) {
              console.error('[PaymentSelection] Microtask: Network error while abandoning order:', err);
            }
          }
          */
        }
      });
    };
  }, []); // Empty dependency array is key.

  const handlePaymentSelection = async (provider) => {
    if (!orderId) {
      addNotification(t('paymentSelection.cannotProceedNoOrderId'), 'error');
      return;
    }

    // Set the flag to indicate a payment attempt is starting.
    sessionStorage.setItem(PAYMENT_IN_PROGRESS_FLAG, 'true');

    setLoadingProvider(provider);
    setError('');

    try {
      const response = await apiFetch(
        `${BASE_URL}/api/payments/generate-payment-url`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ orderId, provider }),
        },
        user.token
      );

      if (response.ok) {
        const data = await response.json();
        if (data.paymentUrl) {
          addNotification(t('paymentSelection.redirecting'), 'success');
          window.location.href = data.paymentUrl; // Redirect to the provider's page
        } else {
          // If we fail to get a URL, remove the flag so the user can still abandon the order.
          sessionStorage.removeItem(PAYMENT_IN_PROGRESS_FLAG);
          throw new Error(t('paymentSelection.noPaymentUrl'));
        }
      } else {
        // If the API call fails, remove the flag.
        sessionStorage.removeItem(PAYMENT_IN_PROGRESS_FLAG);
        const errorData = await response.json();
        throw new Error(errorData.message || t('paymentSelection.generateLinkFailed'));
      }
    } catch (err) {
      // Also remove the flag on any other error.
      sessionStorage.removeItem(PAYMENT_IN_PROGRESS_FLAG);
      setError(err.message);
      addNotification(err.message, 'error');
      setLoadingProvider(null);
    }
  };

  if (error) {
    return (
      <div className="payment-selection-container">
        <h2>{t('paymentSelection.errorTitle')}</h2>
        <p className="error-message">{error}</p>
        <button onClick={() => navigate(`/${lang}`)}>{t('paymentSelection.goToHomepage')}</button>
      </div>
    );
  }

  if (!order) {
    return <div className="payment-selection-container">{t('paymentSelection.loading')}</div>;
  }

  const amountToPay = formatCurrency(order.amountPaidWithCard, order.currency);

  return (
    <div className="payment-selection-container">
      <h2>{t('paymentSelection.title')}</h2>
      <p>{t('paymentSelection.description', { orderId: orderId.slice(-8) })}</p>
      <div className="payment-options">
        <button onClick={() => handlePaymentSelection('rivhit')} disabled={!!loadingProvider} className="payment-option-btn">
          {loadingProvider === 'rivhit' ? t('paymentSelection.processing') : t('paymentSelection.payWithCard', { amount: amountToPay })}
        </button>
        <button onClick={() => handlePaymentSelection('paypal')} disabled={!!loadingProvider} className="payment-option-btn paypal-btn">
          {loadingProvider === 'paypal' ? t('paymentSelection.processing') : t('paymentSelection.payWithPaypal', { amount: amountToPay })}
        </button>
      </div>
    </div>
  );
};

export default PaymentSelection;
